/**
 * components/checkout/shared/stripe-client.ts
 *
 * The ONE Stripe.js client both checkout surfaces use — CheckoutModal (e-book,
 * Distance, /join) and the /checkout/membership page. Extracted verbatim from
 * CheckoutModal.tsx on 2026-09-14 so the two surfaces cannot drift on key
 * resolution, the eighteen-day-outage guard, or the base appearance.
 */
import { loadStripe } from '@stripe/stripe-js';
import {
  CLIENT_PLATFORM_ORIGIN,
  CLIENT_STRIPE_PUBLISHABLE_KEY,
} from '@/lib/generated/client-env';

// Resolved per deploy target at build time into lib/generated/client-env.ts by
// scripts/resolve-client-env.mjs. NOT read from process.env: NEXT_PUBLIC_*
// inlining is not deterministic in this bundle (see that script's header) and a
// silently-empty key here is the exact shape of the eighteen-day outage.
export const PLATFORM_ORIGIN =
  CLIENT_PLATFORM_ORIGIN || process.env.NEXT_PUBLIC_PLATFORM_ORIGIN || '';

// NEXT_PUBLIC_* is inlined by `next build` from process.env at BUILD time.
// From the 2026-08-07 go-live until 2026-08-25 this storefront served a TEST
// publishable key against LIVE PaymentIntents: a blank payment form, no console
// error, no build failure, and not one completed sale. There is no fallback any
// more — scripts/resolve-client-env.mjs REFUSES to build without a key whose
// mode matches the target.
export const PUBLISHABLE_KEY =
  CLIENT_STRIPE_PUBLISHABLE_KEY || process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY || '';
export const KEY_MODE = PUBLISHABLE_KEY.startsWith('pk_live_')
  ? 'live'
  : PUBLISHABLE_KEY.startsWith('pk_test_')
    ? 'test'
    : 'unknown';
export const KEY_PROBLEM =
  KEY_MODE === 'unknown'
    ? PUBLISHABLE_KEY
      ? 'Payments are misconfigured on this site (NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY is not a Stripe publishable key). Please contact support — do not try again.'
      : 'Payments are misconfigured on this site (NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY is not set). Please contact support — do not try again.'
    : null;

// loadStripe('') resolves to null and leaves Elements mounting nothing, silently.
// 2026-09-04 Fix: wrap in .catch() to handle rejection (network failure, CSP violations, etc.)
export const stripePromise = PUBLISHABLE_KEY
  ? loadStripe(PUBLISHABLE_KEY).catch((err) => {
      console.error('Stripe.js failed to load:', err);
      return null;
    })
  : null;

// Stripe Payment/Link/Express appearance — LIGHT, literal values (Stripe needs
// literal strings). No theme branch — light only. The modal uses this as-is.
export const STRIPE_APPEARANCE = {
  theme: 'stripe' as const,
  variables: {
    colorPrimary: '#562096', // --cta
    colorBackground: '#FFFFFF', // field bg — one step lighter than the #FBF8F3 card
    colorText: '#2A2233', // --text-on-cream
    colorDanger: '#C0392B', // --error-text-on-cream
    borderRadius: '8px', // --radius-md
    fontFamily: 'Figtree, system-ui, sans-serif',
  },
};
